import React, { useCallback, useEffect, useRef, useState } from 'react';
import CountUp from 'react-countup';
import { BsEyeSlash } from 'react-icons/bs';
import { CgClose } from 'react-icons/cg';
import { HiFire } from 'react-icons/hi';
import styled, { css, keyframes } from 'styled-components';

import packageJson from '../package.json';
import useApi from './actions/useApi';
import { AppContextProvider } from './appContext';
import ChatIconLight from './assets/conversation-chat-light.svg';
import CommentInput from './components/comment-input';
import Comments from './components/comments';
import NewComment from './components/comments/new-comment';
import Footer from './components/footer';
import PostPurchase from './components/post-purchase';
import PrimaryComment, { COOKIE_NAME } from './components/primary-comment';
import SelectedComment, { SuccessIcon } from './components/selected-comment';
import UserForm from './components/user-form';
import useMetaTags from './hooks/useMetaTags';
import useMouseTracking from './hooks/useMouseTracking';
import { usePageUrl } from './hooks/usePageUrl';
import useSSE from './hooks/useSSE';
import { closeUserForm, selectComment, setThemeMode, setWidgetId } from './store/actions';
import { useStateContext } from './store/stateContext';
import { computeWidgetDisplay } from './utils/widgetDisplay';

import type { Comment, User } from './types';

// Keyframes for animations
const slideInAnimation = keyframes`
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const slideOutAnimation = keyframes`
  from {
    transform: translateY(0);
    opacity: 1;
  }
  to {
    transform: translateY(100%);
    opacity: 0;
  }
`;

const slideInLeftCenterAnimation = keyframes`
  from {
    transform: translateX(-100%);
  }
  to {
    transform: translateX(0);
  }
`;

const slideOutLeftCenterAnimation = keyframes`
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(-100%);
  }
`;

const slideInRightCenterAnimation = keyframes`
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
`;

const slideOutRightCenterAnimation = keyframes`
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(100%);
  }
`;

// Add these after the keyframes
// Styled components
const MainContainer = styled.div<{ position: string; isOpen: boolean }>`
  position: fixed;
  z-index: 999999;
  min-width: ${props => props.isOpen ? '360px' : 'auto'};
  display: flex;
  flex-direction: column;
  ${props => (props.position === 'rightbottom' || props.position === 'rightcenter') && css`
    align-items: flex-end;
  `}
  
  ${props => props.position === 'leftbottom' && css`
    position: fixed;
    bottom: 0;
    left: 0;
    padding: 10px;
    align-items: flex-start;
    @media (max-width: 768px) {
      width: 100%;
      max-width: 100%;
      padding: 0;
      left: 0;
      right: 0;
      align-items: flex-start;
    }
  `}
  
  ${props => props.position === 'rightbottom' && css`
    position: fixed;
    bottom: 0;
    right: 0;
    padding: 10px;
    
    @media (max-width: 768px) {
      width: 100%;
      max-width: 100%;
      padding: 0;
      right: 0;
      left: 0;
      align-items: flex-end;
    }
  `}
  
  ${props => props.position === 'leftcenter' && css`
    left: 0;
    max-width: ${props.isOpen ? '100%' : '360px'};
    min-width: ${props.isOpen ? '360px' : 'auto'};
    top: 50%;
    transform: translateY(-50%);
    max-height: 100%;
    align-items: flex-start;
    @media (max-width: 768px) {
      width: 100%;
      max-width: 100%;
      min-width: 100%;
      left: 0;
      right: 0;
      align-items: flex-start;
    }
  `}
  
  ${props => props.position === 'rightcenter' && css`
    right: 0;
    max-width: ${props.isOpen ? '100%' : '360px'};
    min-width: ${props.isOpen ? '360px' : 'auto'};
    top: 50%;
    transform: translateY(-50%);
    max-height: 100%;
    
    @media (max-width: 768px) {
      width: 100%;
      max-width: 100%;
      min-width: 100%;
      right: 0;
      left: 0;
      align-items: flex-end;
    }
  `}
`;


const CommentsContainer = styled.div<{ position: string; hasInteracted: boolean; isOpen: boolean }>`
  display: flex;
  flex-direction: column;
  width: 360px !important;
  @media (max-width: 768px) {
    width: 100% !important;
  }
  ${props => props.position === 'leftbottom' && css`
    padding: 0;
    ${props.hasInteracted && (props.isOpen ?
      css`animation: ${slideInAnimation} 0.2s ease-out forwards;` :
      css`animation: ${slideOutAnimation} 0.2s ease-out forwards;`
    )}
  `}
  
  ${props => props.position === 'rightbottom' && css`
    ${props.hasInteracted && (props.isOpen ?
      css`animation: ${slideInAnimation} 0.2s ease-out forwards;` :
      css`animation: ${slideOutAnimation} 0.2s ease-out forwards;`
    )}
  `}
  
  ${props => props.position === 'leftcenter' && css`
    ${props.hasInteracted && (props.isOpen ?
      css`animation: ${slideInLeftCenterAnimation} 0.2s ease-out forwards;` :
      css`animation: ${slideOutLeftCenterAnimation} 0.2s ease-out forwards;`
    )}
  `}
  
  ${props => props.position === 'rightcenter' && css`
    ${props.hasInteracted && (props.isOpen ?
      css`animation: ${slideInRightCenterAnimation} 0.2s ease-out forwards;` :
      css`animation: ${slideOutRightCenterAnimation} 0.2s ease-out forwards;`
    )}
  `}
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-row: row;
  gap: 8px !important; /* 0.5rem = 8px */
  align-items: center;
  justify-content: flex-end;
  margin-bottom: 8px !important; /* 0.5rem = 8px */
  width: 100%;
  
  @media (max-width: 768px) {
    justify-content: flex-end;
  }
`;

const ContentContainer = styled.div<{ isDarkMode: boolean; position: string }>`
  background-color: ${props => props.isDarkMode ? 'white' : '#585568'} !important;
  padding: 16px 16px 10px 16px !important; /* 1rem = 16px */
  
  ${props => {
    if (props.position === 'leftbottom') {
      return css`
        border-radius: 16px !important; /* 1rem = 16px */
        border-bottom-left-radius: 0 !important;
        border-bottom-right-radius: 0 !important;
        
        @media (min-width: 768px) {
          border-radius: 16px !important; /* 1.5rem = 24px */
        }
      `;
    } else if (props.position === 'rightbottom') {
      return css`
        border-radius: 16px !important; /* 1rem = 16px */
        border-bottom-left-radius: 0 !important;
        border-bottom-right-radius: 0 !important;
        
        @media (min-width: 768px) {
          border-radius: 16px !important; /* 1.5rem = 24px */
        }
      `;
    } else if (props.position === 'leftcenter') {
      return css`
        border-radius: 16px !important; /* 1rem = 16px */
        border-bottom-left-radius: 0 !important;
        border-top-left-radius: 0 !important;
      `;
    } else if (props.position === 'rightcenter') {
      return css`
        border-radius: 16px !important; /* 1rem = 16px */
        border-bottom-right-radius: 0 !important;
        border-top-right-radius: 0 !important;
      `;
    }
  }}
`;

const ActionButton = styled.button<{ isDarkMode: boolean; width?: string }>`
  border: none;
  background-color: ${props => props.isDarkMode ? 'white' : '#585568'};
  color: ${props => props.isDarkMode ? 'black' : 'white'};
  height: 32px;
  font-size: 12px;
  padding: 8px !important; /* 0.5rem = 8px */
  border-radius: 24px !important; /* 1.5rem = 24px */
  display: flex;
  flex-direction: row;
  gap: 4px !important; /* 0.25rem = 4px */
  align-items: center;
  justify-content: center;
  align-self: flex-end;
  cursor: pointer;
  ${props => props.width && `width: ${props.width};`}
  &:hover {
    background-color: ${props => props.isDarkMode ? 'white' : '#585568'} !important;
    color: ${props => props.isDarkMode ? 'black' : 'white'} !important;
  }
`;

const SuccessMessage = styled.div`
  border-radius: 24px !important; /* 1.5rem = 24px */
  background-color: #23C68D !important;
  padding: 0 16px !important; /* 1rem = 16px */
  margin-top: 8px !important; /* 0.5rem = 8px */
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  color: white;
  font-size: 14px !important;
  line-height: 20px !important;
  letter-spacing: 0px !important;
  font-weight: 700 !important;
  height: 40px !important;
  font-family: Sora !important;
`;

const ToggleButton = styled.button<{
  isDarkMode: boolean;
  position: string;
  hasInteracted: boolean;
  isOpen: boolean;
  hasComments: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: none;
  background-color: ${props =>
    !props.hasComments ? '#5942D1' :
      props.isDarkMode ? 'white' : '#585568'
  };
  color: ${props =>
    !props.hasComments ? 'white' :
      props.isDarkMode ? 'black' : 'white'
  };
  padding: 8px 16px !important; /* 0.5rem = 8px, 1rem = 16px */
  gap: 8px !important; /* 0.5rem = 8px */
  
  ${props => !props.hasComments && css`
    height: 40px;
    width: 40px;
    border-radius: 8px !important; /* 0.5rem = 8px */
  `}
  
  ${props => props.hasComments && css`
    height: ${props.isDarkMode ? '32px' : '48px'};
    
    @media (min-width: 768px) {
      height: 32px;
    }
  `}
  
  ${props => props.position === 'leftbottom' && css`
    margin: 8px !important; /* 0.5rem = 8px */
    border-radius: ${props.hasComments ? '24px' : '16px'} !important;
    height: 40px;

    ${props.hasInteracted && (props.isOpen ?
      css`animation: ${slideOutAnimation} 0.2s ease-out forwards;` :
      css`animation: ${slideInAnimation} 0.2s ease-out forwards;`
    )}
    
    @media (min-width: 768px) {
      margin: 0;
    }
  `}
  
  ${props => props.position === 'rightbottom' && css`
    margin: 8px !important; /* 0.5rem = 8px */
    border-radius: ${props.hasComments ? '24px' : '16px'} !important;
    height: 40px;
    
    ${props.hasInteracted && (props.isOpen ?
      css`animation: ${slideOutAnimation} 0.2s ease-out forwards;` :
      css`animation: ${slideInAnimation} 0.2s ease-out forwards;`
    )}
    
    @media (min-width: 768px) {
      margin: 0;
    }
  `}
  
  ${props => props.position === 'leftcenter' && css`
    border-radius: 24px !important; /* 1.5rem = 24px */
    border-bottom-left-radius: 0 !important;
    border-top-left-radius: 0 !important;
    
    ${props.hasInteracted && (props.isOpen ?
      css`animation: ${slideOutLeftCenterAnimation} 0.2s ease-out forwards;` :
      css`animation: ${slideInLeftCenterAnimation} 0.2s ease-out forwards;`
    )}
  `}
  
  ${props => props.position === 'rightcenter' && css`
    border-radius: 24px !important; /* 1.5rem = 24px */
    border-bottom-right-radius: 0 !important;
    border-top-right-radius: 0 !important;
    
    ${props.hasInteracted && (props.isOpen ?
      css`animation: ${slideOutRightCenterAnimation} 0.2s ease-out forwards;` :
      css`animation: ${slideInRightCenterAnimation} 0.2s ease-out forwards;`
    )}
  `}
  
  &:hover {
    background-color: ${props =>
    !props.hasComments ? '#5942D1' :
      props.isDarkMode ? 'white' : '#585568'
  } !important;
      color: ${props =>
    !props.hasComments ? 'white' :
      props.isDarkMode ? 'black' : 'white'
  } !important;
  }

`;

const ButtonText = styled.p<{ isDarkMode: boolean }>`
  all: revert;
  margin: 0;
  font-family: Sora;
  font-weight: 700;
  font-size: 12px;
  color: ${props => props.isDarkMode ? 'black' : 'white'};
  line-height: 20px;
`;

const SuccessText = styled.p`
  color: white;
  margin: 0;
`;

const IconWrapper = styled.div`
  display: flex;
  width: 22px;
`;

const ChatIcon = styled.img<{ isDarkMode: boolean }>`
  filter: ${props => props.isDarkMode ? 'invert(1)' : 'none'};
`;

const FooterWrapper = styled.div`
  margin-top: 10px !important; /* 0.4rem = 6.4px */
`;

// Root container that will scope all styles to the widget
const WidgetRoot = styled.div`
  font-family: 'Sora', sans-serif;
  * {
    font-family: inherit;
  }
  z-index: 99999999999;

  /* For WebKit browsers (Chrome, Safari, Edge) */
  ::-webkit-scrollbar {
    width: 4px;
    height: 80px;
  }

  ::-webkit-scrollbar-thumb {
    background-color: #625F74;
    border-radius: 40px 0px 0px 0px;
  }

  ::-webkit-scrollbar-track {
    background-color: transparent;
  }

  /* For Firefox */
  * {
    scrollbar-width: thin;
    scrollbar-color: #625F74 transparent !important;
  }
`;

// Update UserFormContainer styling
const UserFormContainer = styled.div<{ hasInteracted: boolean; isOpen: boolean; position?: string }>`
  position: relative;
  padding: 16px !important; /* 1rem = 16px */
  padding-bottom: 24px !important; /* 1.5rem = 24px */
  width: ${props => (props.position === 'leftcenter' || props.position === 'rightcenter') ? '100%' : '100%'};
  max-width: 360px !important;
  z-index: 10000;
  @media (max-width: 768px) {
    width: 100%;
    padding: 0 !important;
    padding-bottom: 0 !important;
    max-width: 100% !important;
  }
`;

interface IAppProps {
  widgetId: string | undefined | null;
  isPurchase?: boolean | undefined;
  widgetAlignment?: string;
}

const App: React.FC<IAppProps> = ({ widgetId }) => {
  const [showPurchase, setShowPurchase] = useState<boolean | undefined>(undefined);

  useSSE();
  useMouseTracking();
  usePageUrl();

  // Detect the device OS
  const isIOS = /iPad|iPhone/.test(navigator.userAgent);

  // Set viewport properties based on the device OS
  const viewportContent = isIOS
    ? 'width=device-width, initial-scale=1, maximum-scale=1'
    : 'width=device-width, initial-scale=1, shrink-to-fit=no';

  // Create a meta tag and append it to the document head
  const metaTag = document.createElement('meta');
  metaTag.name = 'viewport';
  metaTag.content = viewportContent;
  document.head.appendChild(metaTag);

  console.log("User pop widget version - ", packageJson.version)
  // Do not like this but had to hack it to make post purchase complex conditions to work.
  const [systemReady, setSystemReady] = useState(false);
  const [open, setOpen] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [isWritingReview, setIsWritingReview] = useState(false);
  const { isReady, metaData } = useMetaTags();
  const { state, dispatch } = useStateContext();
  const [widgetAlignment, setWidgetAlignment] = useState<string>(state.productInfo?.widget_position || '');
  // const widgetAlignment = state.productInfo?.widget_position || 'leftcenter';
  // const widgetAlignment = 'leftcenter'
  useEffect(() => {
    if (widgetAlignment !== state.productInfo?.widget_position) {
      setWidgetAlignment(state.productInfo?.widget_position || 'leftbottom');
    }
  }
    , [state.productInfo?.widget_position])
  console.log({ state })
  const { getWidgetInfo } = useApi();
  const { addComment } = useApi();
  // Get params from url
  const params = new URLSearchParams(window.location.search);

  const reviewId = params.get('review_id');
  const replyId = params.get('reply_id');
  console.log({ params, replyId, reviewId })
  const [success, setSuccess] = useState<boolean>(false);
  const inactivityTimeoutRef = useRef<number | null>(null);
  const autoCloseTimeoutRef = useRef<number | null>(null);
  const [runCounter, setRunCounter] = useState<boolean>(true);
  const [openPrimaryComment, setOpenPrimaryComment] = useState<boolean>(false);
  const proActiveTimer = state?.productInfo?.proactive_appearance_timer ?? 30;
  // const userFromStore = getItem('user_info');
  useEffect(() => {
    if (state.data?.data?.length > 0 && reviewId) {
      dispatch(selectComment({ commentId: Number(reviewId) }))
      setOpen(true);
    }
  }, [reviewId, replyId, state.data.data])
  const handleSubmit = async (message: string, values: Partial<User> | undefined) => {
    await addComment(`${state.productInfo?.id}`, message, values);
    setSuccess(true);
    setTimeout(() => {
      setSuccess(false);
      setIsWritingReview(false);
    }, 3000);
  }

  useEffect(() => {
    const feedback_widget_display_settings = state.productInfo?.feedback_widget_display_settings ?? {};
    if (feedback_widget_display_settings.url_pattern === null || feedback_widget_display_settings.url_pattern === undefined) {
      setShowPurchase(false);
      return;
    }
    if (!computeWidgetDisplay({ widget_display_action: feedback_widget_display_settings.action || '', widget_display_condition: feedback_widget_display_settings.condition || '', widget_display_url_pattern: feedback_widget_display_settings.url_pattern || '', currentPath: window.location.pathname })) {
      console.log('$$$ Hide post purchase', feedback_widget_display_settings, window.location.pathname);
      setShowPurchase(false);
      // dispatch({ type: 'HIDE_WIDGET' });
      return;
    } else {
      console.log('$$ show post purchase');
      setShowPurchase(true);
    }
  }, [window.location.pathname, state.productInfo?.feedback_widget_display_settings])
  // useEffect(() => {
  //   console.log('comments', state.data.data, userFromStore?.email, runOnce)
  //   if (!reviewId && isReady && state.productInfo) {
  //     if (!runOnce && !state.loading) {
  //       const comments = state.data.data;
  //       if (userFromStore?.email) {
  //         if (comments.length === 0) {
  //           setShowPurchase(isPurchase as boolean);
  //           setRunOnce(true);
  //           return
  //         }
  //         //find if user has already posted a comment
  //         const userComment = comments.find(comment => comment.user.email === userFromStore?.email);
  //         if (userComment) {
  //           setShowPurchase(false);
  //           return
  //         } else {
  //           setShowPurchase(isPurchase as boolean);
  //           setRunOnce(true);
  //         }
  //       } else {
  //         setShowPurchase(isPurchase as boolean);
  //         setRunOnce(true);
  //       }
  //     }
  //   }
  // }, [reviewId, isReady, isPurchase, state.productInfo, state.data.data, userFromStore?.email, state.loading]);
  useEffect(() => {
    const darkModeMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    dispatch(setThemeMode({ mode: darkModeMediaQuery.matches ? 'dark' : 'light' }));

    const handleChange = (e: unknown) => {
      // @ts-expect-error MediaQueryList event type is not properly defined
      dispatch(setThemeMode({ mode: e.matches ? 'dark' : 'light' }));
    };
    darkModeMediaQuery.addEventListener('change', handleChange);

    return () => {
      darkModeMediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  useEffect(() => {
    setTimeout(() => {
      setSystemReady(true);
    }, 1000);
  }, []);

  useEffect(() => {
    dispatch(setWidgetId({ widgetId }));
  }, []);
  console.log('hello', state.productInfo);
  useEffect(() => {
    if (isReady && state.widgetId !== '') {
      const fetchComments = async () => {
        getWidgetInfo(widgetId as string, metaData?.['og:url']);
      };

      fetchComments();
    }
  }, [dispatch, isReady, state.widgetId]);

  const clearTimers = useCallback(() => {
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
      inactivityTimeoutRef.current = null;
    }
    if (autoCloseTimeoutRef.current) {
      clearTimeout(autoCloseTimeoutRef.current);
      autoCloseTimeoutRef.current = null;
    }
    setOpenPrimaryComment(false);
  }, []);

  const handleUserInteraction = useCallback(() => {
    console.log('handleUserInteraction')
    setHasInteracted(true);
    setRunCounter(false);
    setOpenPrimaryComment(false);
    // Clear both inactivity and auto-close timers to stop any auto actions
    clearTimers();
  }, [clearTimers]);

  const resetInactivityTimer = useCallback(() => {
    clearTimers();  // Clear any existing timers before starting new ones
    const cookies = document.cookie.split("; ");
    const isComponentShown = cookies.find(row => row.startsWith(`${COOKIE_NAME}=`));
    if (proActiveTimer && showPurchase !== undefined && !isComponentShown) {
      inactivityTimeoutRef.current = window.setTimeout(() => {
        if (!hasInteracted && !showPurchase) {
          setOpenPrimaryComment(true);
        }

        // // Start the auto-close timer
        // autoCloseTimeoutRef.current = window.setTimeout(() => {
        //   if (!hasInteracted) {
        //     setOpenPrimaryComment(false);
        //   }
        // }, proActiveTimer * 1000); // Auto-close after 20 seconds
      }, (proActiveTimer) * 1000);
    }
  }, [proActiveTimer, hasInteracted, clearTimers, showPurchase]);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
    if (state.data.data.length === 0) {
      setIsWritingReview(true)
    }
    dispatch(closeUserForm());
    handleUserInteraction(); // Mark as interacted after the first click
  };

  const handlePrimaryToggle = () => {
    setOpenPrimaryComment((prevOpen) => !prevOpen);
  }

  const handleCloseReview = () => {
    setIsWritingReview(false);
    if (state.data.data.length === 0) {
      setOpen((prevOpen) => !prevOpen);
    }
    handleUserInteraction();
    dispatch(closeUserForm());
  };

  useEffect(() => {
    // Start the inactivity timer when the component is mounted
    resetInactivityTimer();

    // Cleanup timers on component unmount
    return () => {
      clearTimers();
    };
  }, [resetInactivityTimer, clearTimers]);

  if (!systemReady) return null;

  const isCommentSelected = state.selectedCommentId !== '';
  const isDarkMode = state.themeMode === 'dark';
  console.log({ state, metaData, isReady, url: metaData['og:url'], widgetAlignment })
  const position: 'leftbottom' | 'leftcenter' | 'rightcenter' | 'rightbottom' = widgetAlignment as 'leftbottom' | 'leftcenter' | 'rightcenter' | 'rightbottom';
  // const isLeftAligned = position === 'leftbottom' || position === 'leftcenter';
  if (state.hideWidget && !showPurchase) {
    return null;
  }
  if (!metaData['og:url']) {
    console.error('Missing Og Tag Url,  Hence we cannot load the wdget here')
    return null;
  }
  if (!widgetAlignment) {
    return null;
  }
  const comment: Comment = state.data.data.find((c: Comment) => c.id === state.selectedCommentId) as Comment;
  console.log('nodeenvi', import.meta.env.VITE_NODE_ENV)
  return (
    <WidgetRoot className="userPop-widget">
      <AppContextProvider value={{
        widgetAlignment: widgetAlignment,
        clearTimers: () => {
          clearTimers();
        },
        isPurchase: showPurchase,
        togglePurchaseMode: () => {
          setShowPurchase(!showPurchase);
          handleUserInteraction();
        },
        toggleUserInteraction: () => {
          setHasInteracted(true);
          setRunCounter(false);
          clearTimers();
        },
        openCommentsList: () => {
          setOpen(true);
        }
      }}>
        {import.meta.env.VITE_NODE_ENV === 'development' && (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', position: 'fixed', top: '10px', left: '10px', zIndex: 9999999 }}>
              <div style={{ marginBottom: '10px' }}>
                <button
                  style={{ margin: '0 5px 5px 0', padding: '5px 10px', background: '#4a4a4a', color: 'white', border: 'none', borderRadius: '4px' }}
                  onClick={() => setWidgetAlignment('leftcenter')}
                >
                  Left center
                </button>
                <button
                  style={{ margin: '0 5px 5px 0', padding: '5px 10px', background: '#4a4a4a', color: 'white', border: 'none', borderRadius: '4px' }}
                  onClick={() => setWidgetAlignment('leftbottom')}
                >
                  Left bottom
                </button>
                <button
                  style={{ margin: '0 5px 5px 0', padding: '5px 10px', background: '#4a4a4a', color: 'white', border: 'none', borderRadius: '4px' }}
                  onClick={() => setWidgetAlignment('rightcenter')}
                >
                  Right center
                </button>
                <button
                  style={{ margin: '0 5px 5px 0', padding: '5px 10px', background: '#4a4a4a', color: 'white', border: 'none', borderRadius: '4px' }}
                  onClick={() => setWidgetAlignment('rightbottom')}
                >
                  Right bottom
                </button>
              </div>
            </div>
          </>
        )}

        <MainContainer position={position} isOpen={open || openPrimaryComment} className="main-container">
          {showPurchase ? (
            <PostPurchase widgetId={widgetId as string} />
          ) : (
            <>
              {openPrimaryComment && (
                <>
                  <ButtonContainer>
                    <ActionButton
                      isDarkMode={isDarkMode}
                      onClick={handlePrimaryToggle}
                    >
                      <BsEyeSlash />
                      <ButtonText isDarkMode={isDarkMode}>Hide</ButtonText>
                    </ActionButton>
                  </ButtonContainer>
                  {!reviewId && <PrimaryComment />}
                </>
              )}

              {open && isCommentSelected && comment ? (
                <SelectedComment />
              ) : (
                <CommentsContainer
                  position={position}
                  hasInteracted={hasInteracted}
                  isOpen={open}
                >
                  {open && (
                    <>
                      <ButtonContainer>
                        {!isWritingReview ? (
                          <ActionButton
                            isDarkMode={isDarkMode}
                            onClick={handleToggle}
                          >
                            <BsEyeSlash />
                            <ButtonText isDarkMode={isDarkMode}>Hide</ButtonText>
                          </ActionButton>
                        ) : (
                          <ActionButton
                            isDarkMode={isDarkMode}
                            width="86px"
                            onClick={handleCloseReview}
                          >
                            <CgClose className='h-[16px] w-[16px]' />
                            <ButtonText isDarkMode={isDarkMode}>Close</ButtonText>
                          </ActionButton>
                        )}
                      </ButtonContainer>

                      <ContentContainer isDarkMode={isDarkMode} position={position}>
                        {state.data.data.length > 0 && <Comments isUserFormOpen={state.userForm.isOpen} />}
                        {success ? (
                          <SuccessMessage>
                            <SuccessText>Comment posted!</SuccessText>
                            <SuccessIcon />
                          </SuccessMessage>
                        ) : (
                          state.data.data.length === 0 ? (
                            <NewComment
                              title={state.productInfo?.initial_comment_title}
                              message={state.productInfo?.initial_comment_message}
                            />
                          ) : (
                            <div style={{ marginTop: '0.5rem' }}>
                              <CommentInput
                                inverted={true}
                                containerClasses="w-full"
                                overrideInputbg='bg-white'
                                placeholder='Tell us what you think...'
                                onSubmit={handleSubmit}
                                label='Post my message'
                              />
                            </div>
                          )
                        )}
                        <FooterWrapper>
                          <Footer isDarkMode={isDarkMode} />
                        </FooterWrapper>
                      </ContentContainer>
                    </>
                  )}
                </CommentsContainer>
              )}

              {!open && !openPrimaryComment && (
                <ToggleButton
                  isDarkMode={isDarkMode}
                  position={position}
                  hasInteracted={hasInteracted}
                  isOpen={open}
                  hasComments={state.data.data.length > 0}
                  onClick={handleToggle}
                >
                  {state.data.data.length > 0 ? (
                    <>
                      <ChatIcon
                        src={ChatIconLight}
                        isDarkMode={isDarkMode}
                      />
                      {position !== 'leftcenter' && position !== 'rightcenter' && (
                        <ButtonText isDarkMode={isDarkMode}>
                          {runCounter ? (
                            <CountUp end={state.data.total} duration={2} />
                          ) : (
                            state.data.total
                          )}
                          {state.data.total === 1 ? " comment" : ' comments'}
                        </ButtonText>
                      )}
                    </>
                  ) : (
                    <IconWrapper>
                      <HiFire className='text-xl' />
                    </IconWrapper>
                  )}
                </ToggleButton>
              )}
            </>
          )}
          {state.userForm.isOpen && (
            <UserFormContainer className="userform-container" hasInteracted={hasInteracted} isOpen={state.userForm.isOpen} position={position}>
              <UserForm
                label={state.userForm.label}
                commentText={state.userForm.commentText}
                additionalPayload={state.userForm.additionalPayload}
                onSuccess={(values) => {
                  if (state.userForm.onSuccess) {
                    state.userForm.onSuccess(values);
                  }
                  dispatch(closeUserForm());
                }}
                onClose={() => {
                  if (state.userForm.onClose) {
                    state.userForm.onClose();
                  }
                  dispatch(closeUserForm());
                }}
              />
              {/* user form */}
            </UserFormContainer>
          )}
        </MainContainer>

      </AppContextProvider>
    </WidgetRoot>
  );
};

export default App;
